import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/layouts/AdminLayout';
import { getUser, createUser, updateUser } from '../../../services/userService';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-toastify';
import { FiSave, FiX, FiUser, FiMail, FiLock } from 'react-icons/fi';

const UsersFormPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = !!id;

    const { user: currentUser } = useAuth();
    const isSuperAdmin = currentUser?.role === 'Super Admin';

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        role: 'Admin',
        isActive: true
    });

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        // Redirect if not Super Admin
        if (!isSuperAdmin) {
            toast.error("You don't have permission to access this page");
            navigate('/admin/dashboard');
            return;
        }

        if (isEditMode) {
            fetchUser();
        }
    }, [id, isSuperAdmin, navigate]);

    const fetchUser = async () => {
        try {
            setLoading(true);
            const response = await getUser(id);
            const userData = response.data;

            setFormData({
                name: userData.name || '',
                email: userData.email || '',
                password: '',
                confirmPassword: '',
                role: userData.role || 'Admin',
                isActive: userData.isActive !== undefined ? userData.isActive : true
            });
        } catch (error) {
            console.error('Error fetching user:', error);
            toast.error('Failed to load user data');
            navigate('/admin/users');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate form
        if (!formData.name.trim() || !formData.email.trim()) {
            toast.error('Name and email are required');
            return;
        }

        if (!isEditMode && !formData.password) {
            toast.error('Password is required for new users');
            return;
        }

        if (formData.password && formData.password.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }

        if (formData.password && formData.password !== formData.confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        // Validate email format
        const emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
        if (!emailRegex.test(formData.email)) {
            toast.error('Please enter a valid email address');
            return;
        }

        try {
            setSubmitting(true);

            // Create data object without confirmPassword and isActive (not in schema)
            const userData = {
                name: formData.name,
                email: formData.email,
                role: formData.role
            };

            // Only include password if it's provided
            if (formData.password) {
                userData.password = formData.password;
            }

            if (isEditMode) {
                await updateUser(id, userData);
                toast.success(`User "${formData.name}" updated successfully`);
            } else {
                await createUser(userData);
                toast.success(`User "${formData.name}" created successfully`);
            }

            navigate('/admin/users');
        } catch (error) {
            console.error('Error saving user:', error);
            toast.error(error.response?.data?.message || 'Failed to save user');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AdminLayout>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">
                    {isEditMode ? 'Edit User' : 'Create User'}
                </h1>
            </div>

            {loading ? (
                <div className="flex justify-center py-10">
                    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-600"></div>
                </div>
            ) : (
                <div className="bg-[#f8f8f8] shadow-md rounded-lg overflow-hidden">
                    <form onSubmit={handleSubmit} className="p-6">
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Full Name*
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FiUser className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        className="input pl-10"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Email Address*
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FiMail className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className={`input pl-10 ${isEditMode ? 'bg-gray-50' : ''}`}
                                        required
                                        readOnly={isEditMode}
                                    />
                                </div>
                                {isEditMode && (
                                    <p className="mt-1 text-xs text-gray-500">Email cannot be changed after user creation</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {isEditMode ? 'New Password (leave blank to keep unchanged)' : 'Password*'}
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FiLock className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="password"
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        className="input pl-10"
                                        required={!isEditMode}
                                        minLength={6}
                                        placeholder={isEditMode ? '••••••' : ''}
                                    />
                                </div>
                                <p className="mt-1 text-xs text-gray-500">
                                    Password must be at least 6 characters
                                </p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {isEditMode ? 'Confirm New Password' : 'Confirm Password*'}
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FiLock className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="password"
                                        name="confirmPassword"
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                        className="input pl-10"
                                        required={!isEditMode}
                                        minLength={6}
                                        placeholder={isEditMode ? '••••••' : ''}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Role*
                                </label>
                                <select
                                    name="role"
                                    value={formData.role}
                                    onChange={handleChange}
                                    className="input"
                                    required
                                >
                                    <option value="Admin">Admin</option>
                                    <option value="Super Admin">Super Admin</option>
                                </select>
                            </div>
                        </div>

                        <div className="mt-6 flex items-center justify-end space-x-3">
                            <button
                                type="button"
                                onClick={() => navigate('/admin/users')}
                                className="btn btn-outline flex items-center"
                            >
                                <FiX className="mr-2" /> Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={submitting}
                                className={`btn btn-primary flex items-center ${submitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                            >
                                {submitting ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <FiSave className="mr-2" /> Save
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </AdminLayout>
    );
};

export default UsersFormPage;